import { cn, NoddiIcon } from 'noddi-ui-common';
import { themeColors, webButtonStyling } from 'noddi-ui-web';
import { formatLicensePlate } from 'noddi-util';
import type { ReactNode } from 'react';

interface Props {
  isSelected: boolean;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Make and model ("Tesla Model Y"), not the make alone: two cars of one make must be told apart. */
  carName: string | undefined;
  /** Car illustration between the checkbox and the name. */
  image?: ReactNode;
  licensePlateNumber: string;
  price: string;
}

export const FleetCarSelectionButton = ({ isSelected, onClick, carName, image, licensePlateNumber, price }: Props) => {
  // With a car photo, selected takes the light brand tint, as on the car step: the photo's light backdrop shows as a
  // halo on the solid selected fill. The checkbox carries the selected state.
  const hasSolidSelectedFill = isSelected && !image;

  return (
    <button
      // class for nested interactive elements, use to opt out of the active scale via [data-prevent-card-bounce] for noddiBasicCard scale animation
      data-prevent-card-bounce
      onClick={onClick}
      className={cn(
        'flex min-h-9 items-center justify-between gap-2 border border-primary px-3 py-1 text-start text-primary',
        webButtonStyling,
        isSelected && 'bg-card-light-background',
        hasSolidSelectedFill && 'border-primary-foreground bg-card-selected-background text-card-selected-foreground'
      )}
    >
      {/* One line high: the name truncates on a narrow phone; plate and price sit right, so plates line up across rows. */}
      <div className='flex min-w-0 flex-1 items-center gap-2'>
        <NoddiIcon
          size='small'
          name={isSelected ? 'SquareCheck' : 'Square'}
          color={hasSolidSelectedFill ? themeColors['card-selected-foreground'] : themeColors.primary}
          className='shrink-0'
        />
        {image}
        <span className='truncate'>{carName}</span>
      </div>
      <div className='flex shrink-0 items-center gap-3 whitespace-nowrap tabular-nums'>
        <span>{formatLicensePlate(licensePlateNumber)}</span>
        <span>{price}</span>
      </div>
    </button>
  );
};
